import { supabase } from '@supabaseClient/supabaseClient';
import { UsersClient, IUser } from './UserClient';

export interface Organization {
  id: string;
  name: string;
  join_code: string;
  created_at: string;
  owner: string;
}

export class OrganizationClient {
  static async createOrganization(
    organization: Partial<Organization>
  ): Promise<Organization> {
    const { data, error } = await supabase
      .from('organizations')
      .insert([organization])
      .select();

    if (error) {
      throw error;
    }

    return data[0];
  }

  static async updateOrganization(
    id: string,
    updates: Partial<Pick<Organization, 'name'>>
  ): Promise<Organization> {
    const { data, error } = await supabase
      .from('organizations')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  }

  static async getOrganizationById(id: string): Promise<Organization> {
    const { data, error } = await supabase
      .from('organizations')
      .select()
      .eq('id', id);

    if (error) {
      throw error;
    }

    return data[0];
  }

  static async joinOrganization(joinCode: string) {
    // First, find the organization with the given join code
    const { data: org, error: findError } = await supabase
      .from('organizations')
      .select()
      .eq('join_code', joinCode)
      .single();

    if (findError) {
      throw new Error('Invalid organization code');
    }

    // Get the current user
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('User not authenticated');
    }

    // Switch the active organization only. `personal_organization_id` is set
    // once at signup and must stay pointing at the user's own org so that
    // exitOrganization can always return them home — overwriting it here would
    // strand users who join a second org from an already-joined state.
    const { data: userData, error: updateError } = await supabase
      .from('users')
      .update({ organization_id: org.id })
      .eq('id', user.id)
      .select()
      .single();

    if (updateError) {
      throw updateError;
    }

    return userData;
  }

  static async generateJoinCode(organizationId: string) {
    const joinCode = Math.random().toString(36).substring(2, 8).toUpperCase();

    const { error } = await supabase
      .from('organizations')
      .update({ join_code: joinCode })
      .eq('id', organizationId);

    if (error) {
      throw error;
    }

    return joinCode;
  }

  static async getOrganization(
    organizationId: string
  ): Promise<Organization | null> {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', organizationId)
      .single();

    if (error) {
      // PGRST116 means no rows returned - this is valid when user has no org
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }

    return data;
  }
  static async exitOrganization(userId: string): Promise<IUser> {
    const user = await UsersClient.getUserById(userId);
    const { data, error } = await supabase
      .from('users')
      .update({ organization_id: user.personal_organization_id })
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  }

  static async removeUserFromOrganization(userId: string) {
    // Get the current user's organization first
    const { data: user, error: userError } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', userId)
      .single();

    if (userError) {
      console.error('Error fetching user:', userError);
      throw userError;
    }

    // Call the database function with parameters in correct order
    const { error } = await supabase.rpc('remove_user_from_organization', {
      user_id_to_remove: userId,
      organization_id_param: user.organization_id, // Match the parameter name in the function
    });

    if (error) {
      console.error('Error removing user from organization:', error);
      throw error;
    }

    // Get the updated user data
    const { error: fetchError } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId);

    if (fetchError) {
      throw fetchError;
    }

    return true;
  }
}
